import { useEffect } from "react";

/**
 * Entity Synchronization Service.
 * Provides an in-memory event bus to broadcast and synchronize item updates
 * (such as Shares, Contacts, Memories, Groups, Media, Workflows) across Redux slices,
 * active UI screens, and real-time WebSocket connections.
 */

export type EntityType =
  | "contact"
  | "share"
  | "memory"
  | "group"
  | "media"
  | "message"
  | "event"
  | "workflow";

export type EntityAction = "create" | "update" | "delete" | "invalidate";

/**
 * Payload describing an entity change event.
 */
export interface IEntityChangeEvent<T = any> {
  type: EntityType;
  action: EntityAction;
  id: string;
  data?: T;
  timestamp: number;
  topics?: string[];
}

export type EntityChangeListener = (event: IEntityChangeEvent) => void;
export type TransportSender = (message: { action: string; [key: string]: any }) => void;

/**
 * Client-side event bus and topic subscription coordinator.
 */
class EntitySyncService {
  private listeners: Set<EntityChangeListener> = new Set();
  private activeTopics: Set<string> = new Set();
  private transportSender: TransportSender | null = null;

  /**
   * Subscribes a callback to entity mutation events.
   * @param listener - Function to call on entity change.
   * @returns Unsubscribe function.
   */
  public subscribe(listener: EntityChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Registers a transport sender (e.g. WebSocket send) to forward topic subscription requests.
   * @param sender Transport send function or null when offline.
   */
  public setTransportSender(sender: TransportSender | null): void {
    this.transportSender = sender;
    if (sender) {
      this.resendActiveSubscriptions();
    }
  }

  /**
   * Subscribes to a real-time topic (e.g. "memory:m-1", "group:g-1").
   * @param topic Target topic string
   */
  public subscribeTopic(topic: string): void {
    if (!topic || typeof topic !== "string") return;
    this.activeTopics.add(topic);
    if (this.transportSender) {
      try {
        this.transportSender({
          action: "subscribe_topic",
          topic,
        });
      } catch (err) {
        console.warn("[EntitySyncService] Failed to send subscribe_topic frame:", err);
      }
    }
  }

  /**
   * Unsubscribes from a real-time topic.
   * @param topic Target topic string
   */
  public unsubscribeTopic(topic: string): void {
    if (!topic || typeof topic !== "string") return;
    this.activeTopics.delete(topic);
    if (this.transportSender) {
      try {
        this.transportSender({
          action: "unsubscribe_topic",
          topic,
        });
      } catch (err) {
        console.warn("[EntitySyncService] Failed to send unsubscribe_topic frame:", err);
      }
    }
  }

  /**
   * Re-sends subscription frames for all active topics upon WebSocket reconnection.
   */
  public resendActiveSubscriptions(): void {
    if (!this.transportSender) return;
    for (const topic of this.activeTopics) {
      try {
        this.transportSender({
          action: "subscribe_topic",
          topic,
        });
      } catch (err) {
        console.warn("[EntitySyncService] Failed to re-subscribe topic:", topic, err);
      }
    }
  }

  /**
   * Returns list of currently tracked active topics.
   */
  public getActiveTopics(): string[] {
    return Array.from(this.activeTopics);
  }

  /**
   * Handles an incoming remote entity change received via WebSocket or SSE.
   * Emits the event to local subscribers and Redux slices.
   */
  public handleRemoteEntityChange(
    event: {
      type: string;
      action: string;
      id: string;
      data?: any;
    },
    topics?: string[]
  ): void {
    this.emit({
      type: event.type as EntityType,
      action: event.action as EntityAction,
      id: event.id,
      data: event.data,
      topics,
    });
  }

  /**
   * Emits an entity change event to all subscribers.
   * @param event - The entity change event.
   */
  public emit(event: Omit<IEntityChangeEvent, "timestamp">): void {
    const fullEvent: IEntityChangeEvent = {
      ...event,
      timestamp: Date.now(),
    };
    this.listeners.forEach((listener) => {
      try {
        listener(fullEvent);
      } catch (err) {
        console.error("[EntitySyncService] Error in listener:", err);
      }
    });
  }
}

export const entitySync = new EntitySyncService();

/**
 * React hook to automatically subscribe a component to a granular real-time topic
 * on mount and unsubscribe on unmount.
 * @param topic The topic to track (e.g. `memory:${id}`), or null to skip.
 */
export function useTopicSubscription(topic?: string | null): void {
  useEffect(() => {
    if (!topic) return;
    entitySync.subscribeTopic(topic);
    return () => {
      entitySync.unsubscribeTopic(topic);
    };
  }, [topic]);
}
